import React, {memo, useCallback, useEffect, useMemo, useRef, useState} from "react"
import {ControlModel} from "../../../types/control"
import {VRFControlComponent} from "./component"
import {useRPC} from "../../../hooks/rpc"
import {useDebounce} from "../../../hooks/debounce"
import iconAirDry from "../../../assets/images/icons/controls/vrf/nem.png"
import iconAuto from "../../../assets/images/icons/controls/vrf/auto.png"
import iconFreshPassive from "../../../assets/images/icons/controls/vrf/vantilate-off.png"
import iconFan from "../../../assets/images/icons/controls/vrf/fan.png"
import iconFreshActive from "../../../assets/images/icons/controls/vrf/vantilate-on.png"
import iconCool from "../../../assets/images/icons/controls/vrf/cool.png"
import iconFlap0 from "../../../assets/images/icons/controls/vrf/stop.png"
import iconFlap2 from "../../../assets/images/icons/controls/vrf/down-up.png"
import iconFlap4 from "../../../assets/images/icons/controls/vrf/mixed.png"
import iconHeat from "../../../assets/images/icons/controls/vrf/heat.png"
import iconFan0 from "../../../assets/images/icons/controls/vrf/power-off.png"
import iconFan1 from "../../../assets/images/icons/controls/vrf/slow-poweron.png"
import iconFan2 from "../../../assets/images/icons/controls/vrf/low.png"
import iconFan3 from "../../../assets/images/icons/controls/vrf/medium.png"
import iconFan4 from "../../../assets/images/icons/controls/vrf/high.png"
import iconFan5 from "../../../assets/images/icons/controls/vrf/auto-fan.png"
import iconOff from "../../../assets/images/icons/off-icon.png"
import iconOn from "../../../assets/images/icons/on-icon.png"
import {useApproving} from "../../../hooks/approving"
import { useDispatch, useSelector } from "react-redux"
import { passwordCheckingTimeoutAction } from "../../../store/ui/actions"
import { useMessage } from "../../../hooks/message"

type Props = {
    control: ControlModel
}
export const VRFControl = memo(function VRFControl({control}: Props) {
    const approving = useApproving()
    const [rpc] = useRPC()
    const dispatch = useDispatch();
    const [access, setAccess] = useState(false)
    const { failure } = useMessage();
    const updateVRFTemp = useDebounce((
        id: string,
        power_status: string,
        whole: number,
        fan_speed: string,
        mode: string,
        ventilate: string,
        flap: string
    ) => {
        rpc.UpdateVRF(id, power_status, whole, fan_speed, mode, ventilate, flap).catch(err => {
            if (typeof err === "string") {
                failure(err);
            } else if (err?.message) {
                failure(err.message);
            } else {
                failure("İşlem başarısız");
            }
        });
    }, 500)
    const passwordTimeoutSycle = useSelector(state => state.ui.passwordTimeoutSycle)
    const [powerStatus, setPowerStatus] = useState(control.parameters["power_status"] === "on" ? true : false) //'on' | 'off'
    const [ventilate, setVentilate] = useState(control.parameters["ventilate"] === "open" ? true : false) //'stop' | 'open'
    const [mode, setMode] = useState<number>(
        control.parameters["mode"] === "cool"
            ? 0
            : control.parameters["mode"] === "heat"
            ? 1
            : control.parameters["mode"] === "nem"
            ? 2
            : control.parameters["mode"] === "fan"
            ? 3
            : control.parameters["mode"] === "ai"
            ? 4
            : 0
    ) //"cool" | "fan"| "ai" | "nem" | "heat"
    const [whole, setWhole] = useState<number>(control.parameters["temperature"] || 14)
    const [fan, setFan] = useState<number>(
        control.parameters["fan_speed"] === "slow"
            ? 0
            : control.parameters["fan_speed"] === "low"
            ? 1
            : control.parameters["fan_speed"] === "med"
            ? 2
            : control.parameters["fan_speed"] === "high"
            ? 3
            : control.parameters["fan_speed"] === "pwr"
            ? 4
            : control.parameters["fan_speed"] === "auto"
            ? 5
            : 0
    ) //'slow' | 'low' | 'med' | 'high' | 'auto' | 'pwr'
    const [flap, setFlap] = useState<number>(
        control.parameters["flap"] === "stop"
            ? 0
            : control.parameters["flap"] === "down_up"
            ? 1
            : control.parameters["flap"] === "mixed"
            ? 2
            : 0
    ) // 'stop' | 'down_up' | 'mixed'

    const [vrfUpdate, setVrfUpdate] = useState({
        whole: whole,
        power: powerStatus ? "on" : "off",
        ventilate: ventilate ? "open" : "stop",
        mode:
            mode === 0
                ? "cool"
                : mode === 1
                ? "heat"
                : mode === 2
                ? "nem"
                : mode === 3
                ? "fan"
                : mode === 4
                ? "ai"
                : "cool",
        flap: flap === 0 ? "stop" : flap === 1 ? "down_up" : flap === 2 ? "mixed" : "stop",
        fan:
            fan === 0
                ? "slow"
                : fan === 1
                ? "low"
                : fan === 2
                ? "med"
                : fan === 3
                ? "high"
                : fan === 4
                ? "pwr"
                : fan === 5
                ? "auto"
                : "slow"
    })

    const onClickFan = useCallback(
        (isPlus: boolean) => {
            let newFan = fan
            if (isPlus) {
                newFan++
                if (newFan === 6) {
                    return
                }
            } else {
                newFan--
                if (newFan === -1) {
                    return
                }
            }
            setFan(newFan)
            updateVRFTemp(
                control.id,
                vrfUpdate.power,
                vrfUpdate.whole,
                newFan === 0
                    ? "slow"
                    : newFan === 1
                    ? "low"
                    : newFan === 2
                    ? "med"
                    : newFan === 3
                    ? "high"
                    : newFan === 4
                    ? "pwr"
                    : newFan === 5
                    ? "auto"
                    : "slow",
                vrfUpdate.mode,
                vrfUpdate.ventilate,
                vrfUpdate.flap
            )
        },
        [fan, vrfUpdate, control.id, updateVRFTemp]
    )
    const onClickFlap = useCallback(
        (isPlus: boolean) => {
            let newFlap = flap
            if (isPlus) {
                newFlap++
                if (newFlap === 3) {
                    return
                }
            } else {
                newFlap--
                if (newFlap === -1) {
                    return
                }
            }
            setFlap(newFlap)
            updateVRFTemp(
                control.id,
                vrfUpdate.power,
                vrfUpdate.whole,
                vrfUpdate.fan,
                vrfUpdate.mode,
                vrfUpdate.ventilate,
                newFlap === 0 ? "stop" : newFlap === 1 ? "down_up" : newFlap === 2 ? "mixed" : "stop"
            )
        },
        [flap, vrfUpdate, control.id, updateVRFTemp]
    )

    const onClickTemp = useCallback(
        (isPlus: boolean) => {
            let newWhole = whole
            if (isPlus) {
                newWhole++
            } else {
                newWhole--
            }
            if (newWhole < 15) {
                newWhole = 14
            } else if (newWhole > 32) {
                newWhole = 33
            }
            setWhole(newWhole)
            updateVRFTemp(
                control.id,
                vrfUpdate.power,
                newWhole,
                vrfUpdate.fan,
                vrfUpdate.mode,
                vrfUpdate.ventilate,
                vrfUpdate.flap
            )
        },
        [whole, vrfUpdate, control.id, updateVRFTemp]
    )

    const onClickMode = useCallback(() => {
        let i = mode
        if (i !== 5) {
            i++
        } else {
            if (i === 5) {
                i = 0
            }
        }
        setMode(i)
        updateVRFTemp(
            control.id,
            vrfUpdate.power,
            vrfUpdate.whole,
            vrfUpdate.fan,
            i === 0 ? "cool" : i === 1 ? "heat" : i === 2 ? "nem" : i === 3 ? "fan" : i === 4 ? "ai" : "cool",
            vrfUpdate.ventilate,
            vrfUpdate.flap
        )
    }, [mode, vrfUpdate, control.id, updateVRFTemp])

    const onClickPower = useCallback(() => {
        let newPowerStatus = powerStatus
        if (newPowerStatus) {
            newPowerStatus = false
        } else {
            newPowerStatus = true
        }
        setPowerStatus(newPowerStatus)
        updateVRFTemp(
            control.id,
            newPowerStatus ? "on" : "off",
            vrfUpdate.whole,
            vrfUpdate.fan,
            vrfUpdate.mode,
            vrfUpdate.ventilate,
            vrfUpdate.flap
        )
        if (control.hasError) {
            setAccess(true)
        }
    }, [powerStatus, vrfUpdate, control, updateVRFTemp])

    const onClickVentilate = useCallback(() => {
        let newVentilateStatus = ventilate
        if (newVentilateStatus) {
            newVentilateStatus = false
        } else {
            newVentilateStatus = true
        }
        setVentilate(newVentilateStatus)
        updateVRFTemp(
            control.id,
            vrfUpdate.power,
            vrfUpdate.whole,
            vrfUpdate.fan,
            vrfUpdate.mode,
            newVentilateStatus ? "open" : "stop",
            vrfUpdate.flap
        )
    }, [ventilate, vrfUpdate, control.id, updateVRFTemp])

    const openAreYouSure = useCallback(() => {
        if (control?.parameters?.["password_control"] && !passwordTimeoutSycle) {
            return approving(() => {
                dispatch(passwordCheckingTimeoutAction(true))
                onClickPower()
            })
        }
        onClickPower()
    }, [control, approving, onClickPower, passwordTimeoutSycle, dispatch])

    const powerIcon: string = useMemo(() => {
        if (powerStatus) {
            return iconOn
        } else {
            return iconOff
        }
    }, [powerStatus])

    const ventilateIcon: string = useMemo(() => {
        if (ventilate) {
            return iconFreshActive
        } else {
            return iconFreshPassive
        }
    }, [ventilate])

    const modeIcon: string = useMemo(() => {
        switch (mode) {
            case 0:
                return iconCool
            case 1:
                return iconHeat
            case 2:
                return iconAirDry
            case 3:
                return iconFan
            case 4:
                return iconAuto
            default:
                return iconCool
        }
    }, [mode])

    const fanIcon: string = useMemo(() => {
        switch (fan) {
            case 0:
                return iconFan0
            case 1:
                return iconFan1
            case 2:
                return iconFan2
            case 3:
                return iconFan3
            case 4:
                return iconFan4
            case 5:
                return iconFan5
            default:
                return iconFan0
        }
    }, [fan])

    const flapIcon: string = useMemo(() => {
        switch (flap) {
            case 0:
                return iconFlap0
            case 1:
                return iconFlap2
            case 2:
                return iconFlap4
            default:
                return iconFlap0
        }
    }, [flap])

    useEffect(() => {
        setVrfUpdate({
            whole: whole,
            power: powerStatus ? "on" : "off",
            ventilate: ventilate ? "open" : "stop",
            mode:
                mode === 0
                    ? "cool"
                    : mode === 1
                    ? "heat"
                    : mode === 2
                    ? "nem"
                    : mode === 3
                    ? "fan"
                    : mode === 4
                    ? "ai"
                    : "cool",
            flap: flap === 0 ? "stop" : flap === 1 ? "down_up" : flap === 2 ? "mixed" : "stop",
            fan:
                fan === 0
                    ? "slow"
                    : fan === 1
                    ? "low"
                    : fan === 2
                    ? "med"
                    : fan === 3
                    ? "high"
                    : fan === 4
                    ? "pwr"
                    : fan === 5
                    ? "auto"
                    : "slow"
        })
    }, [powerStatus, fan, flap, mode, ventilate, whole])

    const timeoutRef = useRef<number>()
    const controlRef = useRef<ControlModel>()
    controlRef.current = control

    useEffect(() => {
        // @ts-ignore
        timeoutRef.current = setTimeout(() => {
            if (whole !== controlRef.current.parameters["temperature"]) {
                setWhole(controlRef.current.parameters["temperature"] || 14)
            }
            if (powerStatus !== (controlRef.current.parameters["power_status"] === "on" ? true : false)) {
                setPowerStatus(controlRef.current.parameters["power_status"] === "on" ? true : false)
            }
            if (ventilate !== (controlRef.current.parameters["ventilate"] === "open" ? true : false)) {
                setVentilate(controlRef.current.parameters["ventilate"] === "open" ? true : false)
            }
            if (
                mode !==
                (controlRef.current.parameters["mode"] === "cool"
                    ? 0
                    : controlRef.current.parameters["mode"] === "heat"
                    ? 1
                    : controlRef.current.parameters["mode"] === "nem"
                    ? 2
                    : controlRef.current.parameters["mode"] === "fan"
                    ? 3
                    : controlRef.current.parameters["mode"] === "ai"
                    ? 4
                    : 0)
            ) {
                setMode(
                    controlRef.current.parameters["mode"] === "cool"
                        ? 0
                        : controlRef.current.parameters["mode"] === "heat"
                        ? 1
                        : controlRef.current.parameters["mode"] === "nem"
                        ? 2
                        : controlRef.current.parameters["mode"] === "fan"
                        ? 3
                        : controlRef.current.parameters["mode"] === "ai"
                        ? 4
                        : 0
                )
            }
            if (
                fan !==
                (controlRef.current.parameters["fan_speed"] === "slow"
                    ? 0
                    : controlRef.current.parameters["fan_speed"] === "low"
                    ? 1
                    : controlRef.current.parameters["fan_speed"] === "med"
                    ? 2
                    : controlRef.current.parameters["fan_speed"] === "high"
                    ? 3
                    : controlRef.current.parameters["fan_speed"] === "pwr"
                    ? 4
                    : controlRef.current.parameters["fan_speed"] === "auto"
                    ? 5
                    : 0)
            ) {
                setFan(
                    controlRef.current.parameters["fan_speed"] === "slow"
                        ? 0
                        : controlRef.current.parameters["fan_speed"] === "low"
                        ? 1
                        : controlRef.current.parameters["fan_speed"] === "med"
                        ? 2
                        : controlRef.current.parameters["fan_speed"] === "high"
                        ? 3
                        : controlRef.current.parameters["fan_speed"] === "pwr"
                        ? 4
                        : controlRef.current.parameters["fan_speed"] === "auto"
                        ? 5
                        : 0
                )
            }
            if (
                flap !==
                (controlRef.current.parameters["flap"] === "stop"
                    ? 0
                    : controlRef.current.parameters["flap"] === "down_up"
                    ? 1
                    : controlRef.current.parameters["flap"] === "mixed"
                    ? 2
                    : 0)
            ) {
                setFlap(
                    controlRef.current.parameters["flap"] === "stop"
                        ? 0
                        : controlRef.current.parameters["flap"] === "down_up"
                        ? 1
                        : controlRef.current.parameters["flap"] === "mixed"
                        ? 2
                        : 0
                )
            }
            timeoutRef.current = null
        }, 1000)
        return () => clearTimeout(timeoutRef.current)
    }, [flap, whole, mode, fan, powerStatus, ventilate])

    useEffect(() => {
        if (timeoutRef.current == null) {
            setWhole(control.parameters["temperature"] || 14)
            setPowerStatus(control.parameters["power_status"] === "on" ? true : false)
            setVentilate(control.parameters["ventilate"] === "open" ? true : false)
            setMode(
                control.parameters["mode"] === "cool"
                    ? 0
                    : control.parameters["mode"] === "heat"
                    ? 1
                    : control.parameters["mode"] === "nem"
                    ? 2
                    : control.parameters["mode"] === "fan"
                    ? 3
                    : control.parameters["mode"] === "ai"
                    ? 4
                    : 0
            )
            setFan(
                control.parameters["fan_speed"] === "slow"
                    ? 0
                    : control.parameters["fan_speed"] === "low"
                    ? 1
                    : control.parameters["fan_speed"] === "med"
                    ? 2
                    : control.parameters["fan_speed"] === "high"
                    ? 3
                    : control.parameters["fan_speed"] === "pwr"
                    ? 4
                    : control.parameters["fan_speed"] === "auto"
                    ? 5
                    : 0
            )
            setFlap(
                control.parameters["flap"] === "stop"
                    ? 0
                    : control.parameters["flap"] === "down_up"
                    ? 1
                    : control.parameters["flap"] === "mixed"
                    ? 2
                    : 0
            )
        }
    }, [control])

    useEffect(() => {
        let newValue = access
        if (newValue) {
            const timer = setTimeout(() => {
                newValue = false
                setAccess(newValue)
            }, 2000)
            return () => clearTimeout(timer)
        }
    }, [access])

    return (
        <VRFControlComponent
            control={control}
            onClickPower={openAreYouSure}
            powerIcon={powerIcon}
            onClickVentilate={onClickVentilate}
            ventilateIcon={ventilateIcon}
            disability={powerStatus}
            onClickMode={onClickMode}
            modeIcon={modeIcon}
            onClickTemp={onClickTemp}
            tempValue={whole.toString()}
            onClickFan={onClickFan}
            fanIcon={fanIcon}
            onClickFlap={onClickFlap}
            flapIcon={flapIcon}
            accessDenied={access}
        />
    )
})
